/**
 * Social-link drawer for the element insertion engine.
 *
 * Opens after inserting or duplicating a `.cs-social-link` so the user can
 * immediately tune the destination URL, aria label, or swap platforms. The
 * stored HTML remains canonical: mutations parse with DOMParser, preserve the
 * selected icon eid, sanitize user strings, and strip builder-only attrs before
 * committing through the store.
 */

import { eventBus } from '../../core/events.js';
import { sanitizeHref } from '../../core/sanitizer.js';
import { store } from '../../core/state.js';
import { bridge } from '../../editing/bridge.js';
import {
  escapeForAttrSelector,
  injectEditableAttrs,
  stripTempAttrs
} from '../../editing/editable-utils.js';
import { resolveSectionByInstance } from '../../editing/section-utils.js';
import {
  getSocialPlatform,
  socialLinkAdapter
} from '../../editing/adapters/social-link-adapter.js';
import { openSocialLinkPicker } from '../social-link-picker.js';
import { openSectionDrawer } from './drawer.js';

function parseRoot(html) {
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  return doc.body.firstElementChild || null;
}

function findSocialLink(root, eid) {
  if (!root || !eid) return null;
  let target = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
  if (!target) {
    injectEditableAttrs(root);
    target = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
  }
  return target?.closest?.(socialLinkAdapter.itemSelector) || null;
}

function readSocialLink(sectionHtml, eid) {
  const root = parseRoot(sectionHtml);
  const link = findSocialLink(root, eid);
  if (!link) return null;
  return {
    href: link.getAttribute('href') || '',
    ariaLabel: link.getAttribute('aria-label') || ''
  };
}

function updateSocialLinkAttrs(sectionHtml, eid, attrs) {
  const root = parseRoot(sectionHtml);
  const link = findSocialLink(root, eid);
  if (!link) return { html: sectionHtml, changed: false, found: false };

  let changed = false;
  if (Object.prototype.hasOwnProperty.call(attrs, 'href')) {
    const nextHref = sanitizeHref(attrs.href);
    if ((link.getAttribute('href') || '') !== nextHref) {
      if (nextHref) link.setAttribute('href', nextHref);
      else link.removeAttribute('href');
      changed = true;
    }
  }

  if (Object.prototype.hasOwnProperty.call(attrs, 'ariaLabel')) {
    const nextLabel = String(attrs.ariaLabel || '').trim();
    if ((link.getAttribute('aria-label') || '') !== nextLabel) {
      if (nextLabel) link.setAttribute('aria-label', nextLabel);
      else link.removeAttribute('aria-label');
      changed = true;
    }
  }

  stripTempAttrs(root);
  return { html: changed ? root.innerHTML : sectionHtml, changed, found: true };
}

function platformSvg(platformId) {
  const html = socialLinkAdapter.contentRecipe({ platform: platformId });
  const root = parseRoot(html);
  return root?.querySelector?.('svg.cs-social-img') || null;
}

function replaceSocialLinkPlatform(sectionHtml, eid, platformId) {
  const platform = getSocialPlatform(platformId);
  const nextSvg = platformSvg(platformId);
  if (!platform || !nextSvg) return { html: sectionHtml, changed: false, found: false };

  const root = parseRoot(sectionHtml);
  const link = findSocialLink(root, eid);
  if (!link) return { html: sectionHtml, changed: false, found: false };

  const currentIcon =
    link.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`) ||
    link.querySelector('.cs-social-img');
  if (!currentIcon) return { html: sectionHtml, changed: false, found: false };

  const selectedEid = currentIcon.getAttribute('data-pb-eid') || eid;
  nextSvg.setAttribute('data-pb-eid', selectedEid);
  currentIcon.replaceWith(root.ownerDocument.importNode(nextSvg, true));

  const nextHref = sanitizeHref(platform.defaultHref);
  if (nextHref) link.setAttribute('href', nextHref);
  else link.removeAttribute('href');
  link.setAttribute(
    'aria-label',
    platform.ariaLabel || `visit ${platform.label || platformId} profile`
  );

  stripTempAttrs(root);
  return { html: root.innerHTML, changed: true, found: true };
}

function iframeRectForEid(eid, fallbackRect) {
  try {
    const doc = bridge._iframe?.contentDocument;
    const el = doc?.querySelector?.(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      return { top: r.top, left: r.left, width: r.width, height: r.height };
    }
  } catch {
    // Same-origin access is expected, but fall back quietly if a re-render is mid-flight.
  }
  return fallbackRect || { top: 0, left: 0, width: 1, height: 1 };
}

function makeSyntheticAnchorFromIframeRect(rect) {
  const iframe = bridge._iframe || document.getElementById('pb-preview');
  if (!iframe) return null;
  const iframeRect = iframe.getBoundingClientRect();
  const r = rect || { top: 0, left: 0, width: 1, height: 1 };

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-hidden', 'true');
  btn.tabIndex = -1;
  btn.style.position = 'fixed';
  btn.style.left = `${iframeRect.left + Number(r.left || 0)}px`;
  btn.style.top = `${iframeRect.top + Number(r.top || 0)}px`;
  btn.style.width = `${Math.max(1, Number(r.width || 1))}px`;
  btn.style.height = `${Math.max(1, Number(r.height || 1))}px`;
  btn.style.padding = '0';
  btn.style.margin = '0';
  btn.style.border = '0';
  btn.style.background = 'transparent';
  btn.style.opacity = '0';
  btn.style.pointerEvents = 'none';
  document.body.appendChild(btn);
  return btn;
}

function reselectEditable(instanceId, eid) {
  requestAnimationFrame(() => {
    bridge.send('reselect-editable', { instanceId, eid });
    bridge.send('query-element-rect', { instanceId, eid });
  });
}

function commitSocialLinkChange({ instanceId, eid, label, mutate, onSuccess, showToast }) {
  const target = resolveSectionByInstance(instanceId);
  if (!target?.section?.html) return false;

  const result = mutate(target.section.html);
  if (!result?.found) {
    showToast?.('Social icon not found', { type: 'error' });
    return false;
  }
  if (!result.changed) return false;

  store.update(target.path, result.html, label);
  eventBus.emit('preview:render-request', { preserveScroll: true });
  reselectEditable(instanceId, eid);
  onSuccess?.();
  return true;
}

/**
 * Open the social-link drawer anchored near the selected icon.
 *
 * @param {{
 *   instanceId: string,
 *   eid: string,
 *   sourceRect?: {top:number,left:number,width?:number,height?:number},
 *   showToast?: Function
 * }} opts
 */
export function openSocialLinkDrawer(opts = {}) {
  const { instanceId, eid, sourceRect, showToast } = opts;
  if (!instanceId || !eid) return () => {};

  const anchorRect = iframeRectForEid(eid, sourceRect);
  const anchorEl = makeSyntheticAnchorFromIframeRect(anchorRect);
  const triggerEl = makeSyntheticAnchorFromIframeRect(sourceRect || anchorRect);
  if (!anchorEl || !triggerEl) {
    anchorEl?.remove();
    triggerEl?.remove();
    return () => {};
  }

  return openSectionDrawer({
    id: `social-link:${instanceId}:${eid}`,
    title: 'Social Link',
    anchorEl,
    triggerEl,
    render: (bodyEl) => renderSocialLinkDrawer(bodyEl, { instanceId, eid, sourceRect, showToast }),
    beforeClose: () => {
      anchorEl.remove();
      triggerEl.remove();
    },
    onClose: () => {
      anchorEl.remove();
      triggerEl.remove();
    }
  });
}

/**
 * Render drawer body controls.
 *
 * @param {HTMLElement} container
 * @param {{ instanceId: string, eid: string, sourceRect?: object, showToast?: Function }} ctx
 */
export function renderSocialLinkDrawer(container, ctx) {
  const { instanceId, eid, sourceRect, showToast } = ctx || {};
  container.innerHTML = '';

  const target = resolveSectionByInstance(instanceId);
  const values = target?.section?.html ? readSocialLink(target.section.html, eid) : null;

  const wrap = document.createElement('div');
  wrap.className = 'pb-spi-drawer-section pb-social-link-drawer';
  container.appendChild(wrap);

  if (!values) {
    const empty = document.createElement('p');
    empty.className = 'pb-social-link-empty';
    empty.textContent = 'Social icon not found.';
    wrap.appendChild(empty);
    return () => {};
  }

  const hrefField = field('Destination URL', 'url', values.href);
  const ariaField = field('Accessible label', 'text', values.ariaLabel);
  const status = document.createElement('p');
  status.className = 'pb-social-link-status';
  status.setAttribute('aria-live', 'polite');

  const actions = document.createElement('div');
  actions.className = 'pb-social-link-actions';
  const changePlatformBtn = document.createElement('button');
  changePlatformBtn.type = 'button';
  changePlatformBtn.className = 'pb-social-link-secondary';
  changePlatformBtn.textContent = 'Change platform';
  actions.appendChild(changePlatformBtn);

  wrap.append(hrefField.root, ariaField.root, actions, status);

  function setStatus(message, type = 'info') {
    status.textContent = message || '';
    status.dataset.type = type;
  }

  function commitAttrs() {
    const rawHref = hrefField.input.value.trim();
    const safeHref = sanitizeHref(rawHref);
    if (rawHref && !safeHref) {
      hrefField.input.classList.add('pb-gp-input-invalid');
      setStatus('Enter a safe URL, path, email, phone, or fragment.', 'error');
      showToast?.('That URL is not allowed', { type: 'error' });
      return;
    }
    hrefField.input.classList.remove('pb-gp-input-invalid');

    const ariaLabel = ariaField.input.value.trim();
    if (!ariaLabel) {
      ariaField.input.classList.add('pb-gp-input-invalid');
      setStatus('Add a short accessible label.', 'error');
      showToast?.('Social icons need an accessible label', { type: 'error' });
      return;
    }
    ariaField.input.classList.remove('pb-gp-input-invalid');

    const changed = commitSocialLinkChange({
      instanceId,
      eid,
      label: 'Edit social link',
      showToast,
      mutate: (html) =>
        updateSocialLinkAttrs(html, eid, {
          href: safeHref,
          ariaLabel
        }),
      onSuccess: () => setStatus('Saved', 'success')
    });
    if (!changed) setStatus('');
  }

  hrefField.input.addEventListener('change', commitAttrs);
  ariaField.input.addEventListener('change', commitAttrs);
  for (const input of [hrefField.input, ariaField.input]) {
    input.addEventListener('input', () => {
      input.classList.remove('pb-gp-input-invalid');
      setStatus('');
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitAttrs();
        input.blur();
      }
    });
  }

  changePlatformBtn.addEventListener('click', (e) => {
    e.preventDefault();
    openSocialLinkPicker(iframeRectForEid(eid, sourceRect), {
      onPick: (platformId) => {
        commitSocialLinkChange({
          instanceId,
          eid,
          label: 'Change social platform',
          showToast,
          mutate: (html) => replaceSocialLinkPlatform(html, eid, platformId),
          onSuccess: () => {
            const fresh = resolveSectionByInstance(instanceId);
            const next = fresh?.section?.html ? readSocialLink(fresh.section.html, eid) : null;
            if (next) {
              hrefField.input.value = next.href;
              ariaField.input.value = next.ariaLabel;
            }
            setStatus('Platform changed', 'success');
          }
        });
      }
    });
  });

  return () => {};
}

function field(label, type, value) {
  const root = document.createElement('label');
  root.className = 'pb-gp-field pb-social-link-field';

  const labelEl = document.createElement('span');
  labelEl.className = 'pb-gp-label';
  labelEl.textContent = label;

  const input = document.createElement('input');
  input.className = 'pb-gp-input';
  input.type = type;
  input.value = value || '';
  input.spellcheck = false;

  root.append(labelEl, input);
  return { root, input };
}
